import React, { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { Paper, Box, Button, Typography, Stack, Container, Divider } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { AuthContext } from "../../context/auth-context/AuthContext";
import dayjs from "dayjs";
import "dayjs/locale/de";
import useResponsive from "../../hooks/useResponsive";

import LoadingTable from "../../components/general/LoadingTable";
import InfoSection, { InformationField } from "../../components/general/InfoSection";
import EditEventDialog from "../../components/events/eventDetails/EditEventDialog";
import EventChip from "../../components/events/EventChip";
import EventParticipants from "../../components/events/eventDetails/EventParticipants";
import AddMembersField from "../../components/events/eventDetails/AddMembersField";

import { MembersFieldDto } from "../../types/membersTypes";

import WorkingWeekendParticipantsTable from "../../components/events/workingweekend/WorkingWeekendParticipantsTable";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import AddWorkingWeekendParticipant from "../../components/events/workingweekend/AddWorkingWeekendParticipant";
import useMembers from "../../hooks/members/useMembers";
import { useEventDetails } from "../../hooks/events/useEventDetails";
import DeleteEventDialog from "../../components/events/eventDetails/DeleteEventDialog";
import WorkingWeekendSignUpDialog from "../../components/events/workingweekend/WorkingWeekendSignUpDialog";
import EventRegistrationButton from "../../components/events/EventRegistrationButton";
import { checkIfEventRegistrationIsOpen, checkIfUserIsSignedUpForEvent } from "../../utils/EventsUtils";

/**
 * Renders the page for displaying the details of a given event as well as the participants
 * @param props id in URL
 * @returns the page to display the details of an event
 */
const EventDetails: React.FunctionComponent = () => {
  const { auth } = useContext(AuthContext);
  const { id } = useParams<{ id: string }>();
  const isMobile = useResponsive("down", "md");

  const hasEventPermission = doesPermissionsHaveSomeOf(auth.permissions, [8]);

  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [isWorkingWeekendSignUpOpen, setIsWorkingWeekendSignUpOpen] = useState(false);

  const {
    eventDetails,
    updateEventDetails,
    signUserOutFromEvent,
    signUserUpForEvent,
    deleteEvent,
    signUserUpForWorkingWeekend,
  } = useEventDetails(Number(id));
  const { members } = useMembers();

  // Get the user's registration details for the working weekend, used to display the registration information
  const usersWWRegistrationDetails = eventDetails?.wwMembers.find((member) => member.memberId === auth.userID);

  // Filter members to only those that are not already participants of the event or organizers, used for the add participant field
  const membersToAdd = members.filter(
    (member) =>
      !eventDetails?.members.some((m) => m.memberId === member.memberId) &&
      !eventDetails?.organizers.some((o) => o.memberId === member.memberId)
  );

  // Filter members to only those that are not already participants of the working weekend, used for the add participant field for working weekends
  const membersToAddForWW = members.filter(
    (member) => !eventDetails?.wwMembers.some((m) => m.memberId === member.memberId)
  );

  // Map members to the type needed for the display of organizers in the event details, used for the display of organizers in the event details and the edit event dialog
  const membersForOrganizers: MembersFieldDto[] = members.map((member) => ({
    memberId: member.memberId,
    firstname: member.firstname,
    lastname: member.lastname,
    memberStatus: member.memberStatus,
  }));

  // Boolean to check if the user is an organizer of the event, used to determine if the user can see the participant list for working weekends and the add participant field for working weekends
  const isEventOrganizer = eventDetails?.organizers.some((organizer) => organizer.memberId === auth.userID);

  // Boolean to check if the user is signed up for the event
  const isUserSignedUp = checkIfUserIsSignedUpForEvent(eventDetails, auth.userID ? auth.userID : null);

  // Boolean to check if the registration for the event is open
  const isRegistrationOpen = checkIfEventRegistrationIsOpen(eventDetails);

  // Boolean to check if the registration for the event is full, only revevant for not working weekends
  const isRegistrationFull = eventDetails?.maximumParticipants
    ? eventDetails.members.length >= eventDetails.maximumParticipants
    : false;

  /**
   * Function to render the registration period of the event in a user friendly way, checks if the registration from and to dates are set and formats them accordingly
   * @returns A string representing the registration period of the event, formatted in a user friendly way, if the registration from and to dates are not set, returns "-"
   */
  const renderRegistrationPeriod = (): string => {
    if (!eventDetails) {
      return "-";
    }
    const registrationFrom = eventDetails.registrationFrom
      ? dayjs(eventDetails.registrationFrom).locale("de").format("DD.MM.YYYY HH:mm")
      : null;
    const registrationTo = eventDetails.registrationTo
      ? dayjs(eventDetails.registrationTo).locale("de").format("DD.MM.YYYY HH:mm")
      : null;
    if (registrationFrom && registrationTo) {
      return `${registrationFrom} - ${registrationTo}`;
    }

    if (registrationFrom && !registrationTo) {
      return `ab ${registrationFrom}`;
    }

    if (!registrationFrom && registrationTo) {
      return `bis ${registrationTo}`;
    }
    return "-";
  };

  // Fields for the display of event information
  const displayFields: Array<InformationField> = [
    {
      label: "Anmeldung",
      value: renderRegistrationPeriod(),
      type: "text",
    },
    {
      label: "Beginn",
      value:
        eventDetails && eventDetails.eventBeginn
          ? dayjs(eventDetails.eventBeginn).locale("de").format("DD.MM.YYYY")
          : null,
      type: "text",
    },
    {
      label: "Ende",
      value:
        eventDetails && eventDetails.eventEnd ? dayjs(eventDetails.eventEnd).locale("de").format("DD.MM.YYYY") : null,
      type: "text",
    },
    {
      label: "Startzeit",
      value:
        eventDetails && eventDetails.startTime
          ? dayjs(eventDetails.eventBeginn)
              .set("hours", parseInt(eventDetails.startTime.split(":")[0]))
              .set("minutes", parseInt(eventDetails.startTime.split(":")[1]))
              .locale("de")
              .format("HH:mm")
          : null,
      type: "text",
    },
    {
      label: "Endzeit",
      value:
        eventDetails && eventDetails.endTime
          ? dayjs(eventDetails.eventEnd)
              .set("hours", parseInt(eventDetails.endTime.split(":")[0]))
              .set("minutes", parseInt(eventDetails.endTime.split(":")[1]))
              .locale("de")
              .format("HH:mm")
          : null,
      type: "text",
    },
    {
      label: "Ort",
      value: eventDetails ? eventDetails.location : null,
      type: "text",
    },
    {
      label: "Beschreibung",
      value: eventDetails ? (eventDetails.description ? eventDetails.description : null) : null,
      type: "multi",
    },
    {
      label: "Maximale Teilnehmer",
      value: eventDetails && eventDetails.maximumParticipants ? eventDetails.maximumParticipants.toString() : null,
      type: "text",
    },
    {
      label: "Organisatoren",
      value: eventDetails?.organizers && eventDetails.organizers.length > 0 ? eventDetails.organizers : undefined,
      type: "memberList",
    },
  ];

  // Fields for the display of WW information
  const displayWWFields: Array<InformationField> = [
    {
      label: "Beginn",
      value:
        eventDetails && eventDetails.eventBeginn
          ? dayjs(eventDetails.eventBeginn).locale("de").format("DD.MM.YYYY")
          : null,
      type: "text",
    },
    {
      label: "Ende",
      value:
        eventDetails && eventDetails.eventEnd ? dayjs(eventDetails.eventEnd).locale("de").format("DD.MM.YYYY") : null,
      type: "text",
    },
    {
      label: "Ort",
      value: eventDetails ? eventDetails.location : null,
      type: "text",
    },
    {
      label: "Beschreibung",
      value: eventDetails ? (eventDetails.description ? eventDetails.description : null) : null,
      type: "multi",
    },
  ];

  // Fields for the registration information
  const wwRegistrationFields: Array<InformationField> = [
    {
      label: "Anreise",
      value:
        isUserSignedUp && usersWWRegistrationDetails && eventDetails?.type === "WW"
          ? usersWWRegistrationDetails.arrival
          : null,
      type: "text",
    },
    {
      label: "Geplante Abreise",
      value:
        isUserSignedUp && usersWWRegistrationDetails && eventDetails?.type === "WW"
          ? usersWWRegistrationDetails.departure
          : null,
      type: "text",
    },
    {
      label: "Auto",
      value:
        isUserSignedUp && usersWWRegistrationDetails && eventDetails?.type === "WW"
          ? usersWWRegistrationDetails.car
            ? "Ja, " + usersWWRegistrationDetails.seats
            : "Nein"
          : null,
      type: "text",
    },
    {
      label: "Vegetarier",
      value:
        isUserSignedUp && usersWWRegistrationDetails && eventDetails?.type === "WW"
          ? usersWWRegistrationDetails.isVegetarian
            ? "Ja"
            : "Nein"
          : null,
      type: "text",
    },
    {
      label: "Bemerkungen",
      value:
        isUserSignedUp && usersWWRegistrationDetails && eventDetails?.type === "WW"
          ? usersWWRegistrationDetails.comment
          : null,
      type: "text",
    },
  ];

  /**
   * Handles the onClose event of the edit dialog
   */
  const handleDialogClose = () => {
    setEditDialogOpen(false);
  };

  /**
   * Function is called to add a participant to the event
   * @param participant the participant to add
   */
  const addParticipant = (participant: MembersFieldDto) => {
    signUserUpForEvent(participant.memberId);
  };

  /**
   * Function is called to remove a participant from the event
   * @param participant the participant to remove
   */
  const removeParticipant = (participant: MembersFieldDto) => {
    signUserOutFromEvent(participant.memberId);
  };

  /**
   * Function is called to delete the event, after the deletion the delete dialog is closed
   */
  const handleDeleteEvent = async () => {
    await deleteEvent();
    setDeleteOpen(false);
  };

  // Function to handle the sign up for an event, if the event is a working weekend, the working weekend sign up dialog is opened, otherwise the user is signed up for the event directly
  const handleSignUpForEvent = async () => {
    if (eventDetails?.type === "WW") {
      setIsWorkingWeekendSignUpOpen(true);
    } else {
      await signUserUpForEvent(auth.userID ? auth.userID : -1);
    }
  };

  // Function to handle the sign out from an event
  const handleSignOutFromEvent = async () => {
    if (auth.userID === null) {
      // Theoretically this never happens, only for type safety
      return;
    }
    await signUserOutFromEvent(auth.userID);
  };

  // Function to handle the removal of a WW member
  const removeParticipantFromWW = async (memberId: number) => {
    await signUserOutFromEvent(memberId);
  };

  /**
   * Renders the page
   */
  return (
    <Container maxWidth="lg" sx={{ maxWidth: isMobile ? "95%" : "100%", paddingBottom: 5 }}>
      {eventDetails ? (
        <>
          <WorkingWeekendSignUpDialog
            open={isWorkingWeekendSignUpOpen}
            handleClose={() => setIsWorkingWeekendSignUpOpen(false)}
            ww={eventDetails}
            onSubmit={signUserUpForWorkingWeekend}
            memberId={auth.userID ? auth.userID : -1}
          />
          <Stack direction={isMobile ? "column" : "row"} justifyContent={"space-between"} sx={{ pb: 1 }}>
            <Typography fontWeight={"bold"} variant="h5">
              Termininformationen - {eventDetails ? eventDetails.eventName : null}
            </Typography>
            {hasEventPermission || isEventOrganizer ? (
              <Stack direction={"row"} spacing={2} justifyContent={"space-between"}>
                {eventDetails.eventEnd && dayjs(eventDetails.eventEnd).isBefore(dayjs()) ? null : (
                  <Button
                    variant="contained"
                    onClick={() => setDeleteOpen(true)}
                    startIcon={<Delete />}
                    sx={{ fontSize: 10, fontWeight: "bold" }}
                    color={"error"}
                  >
                    löschen
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => setEditDialogOpen(true)}
                  startIcon={<Edit />}
                  sx={{ fontSize: 10, fontWeight: "bold" }}
                  color={"primary"}
                >
                  bearbeiten
                </Button>
              </Stack>
            ) : null}
          </Stack>
          <Paper>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              sx={{ pt: 2, ml: 3, mr: 3 }}
              alignItems={"center"}
            >
              <Typography variant="h6" color="primary" fontWeight={"bold"}>
                Details
              </Typography>
              <EventChip type={eventDetails ? eventDetails.type : "Sonstige"} sx={{ ml: 3 }} size="medium" />
            </Stack>
            <Box sx={{ ml: 3, mr: "auto", pt: 1, pb: 4, maxWidth: 600 }}>
              <InfoSection fields={eventDetails?.type === "WW" ? displayWWFields : displayFields} />
            </Box>
            {isUserSignedUp && eventDetails?.type === "WW" ? (
              <>
                <Divider sx={{ width: "95%", margin: "auto", borderColor: "#f6891f" }} />
                <Box sx={{ ml: 3, mb: 3 }}>
                  <Typography variant="h6" color="primary" fontWeight={"bold"} sx={{ pt: 2 }}>
                    Anmeldedaten
                  </Typography>
                  <Typography sx={{ pb: 2 }}>
                    Du bist angemeldet!{" "}
                    {isRegistrationOpen
                      ? null
                      : "Da die Anmeldephase abgelaufen ist, kannst du dich nicht mehr abmelden. "}
                    Deine Anmeldedaten:
                  </Typography>
                  <InfoSection fields={wwRegistrationFields} />
                </Box>
              </>
            ) : null}
            {eventDetails.members.length > 0 && eventDetails.type !== "WW" ? (
              <>
                <Divider sx={{ width: "95%", margin: "auto", borderColor: "#f6891f" }} />
                <Typography variant="h6" color="primary" fontWeight={"bold"} sx={{ pt: 2, ml: 3 }}>
                  Teilnehmerliste ({eventDetails.members.length})
                </Typography>
                <Box sx={{ ml: 3, mr: 3, pb: 3, pt: 1 }}>
                  <EventParticipants
                    participants={eventDetails.members}
                    removeParticipant={removeParticipant}
                    hasPermission={hasEventPermission || isEventOrganizer}
                  />
                </Box>
              </>
            ) : eventDetails.wwMembers.length > 0 &&
              eventDetails.type === "WW" &&
              (hasEventPermission || isEventOrganizer) ? (
              <>
                <Divider sx={{ width: "95%", margin: "auto", borderColor: "#f6891f" }} />
                <Typography variant="h6" color="primary" fontWeight={"bold"} sx={{ pt: 2, ml: 3 }}>
                  Teilnehmerliste ({eventDetails.wwMembers.length})
                </Typography>
                {hasEventPermission || isEventOrganizer ? (
                  <Box sx={{ ml: 3, mr: 3, pb: 3, pt: 1 }}>
                    <WorkingWeekendParticipantsTable
                      workingWeekendParticipants={eventDetails.wwMembers}
                      onRemoveParticipant={removeParticipantFromWW}
                    />
                  </Box>
                ) : null}
              </>
            ) : null}
          </Paper>
          <Stack
            sx={{
              justifyContent: "space-between",
              ml: isMobile ? 0 : 3,
              mt: 2,
            }}
            alignItems={isMobile ? "start" : "center"}
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 2 : 0}
          >
            <EventRegistrationButton
              isUserSignedUp={isUserSignedUp}
              isRegistrationOpen={isRegistrationOpen}
              isRegistrationFull={isRegistrationFull}
              onSignUp={handleSignUpForEvent}
              onSignOut={handleSignOutFromEvent}
              size={"medium"}
            />
            <>
              {hasEventPermission || isEventOrganizer ? (
                eventDetails?.type === "WW" ? (
                  <AddWorkingWeekendParticipant
                    members={membersToAddForWW}
                    participants={eventDetails.wwMembers}
                    ww={eventDetails}
                    signUserUpForWorkingWeekend={signUserUpForWorkingWeekend}
                  />
                ) : (
                  <AddMembersField
                    members={membersToAdd}
                    participants={eventDetails.members}
                    addParticipant={addParticipant}
                  />
                )
              ) : null}
            </>
          </Stack>
          <EditEventDialog
            open={editDialogOpen}
            onClose={handleDialogClose}
            event={eventDetails}
            members={membersForOrganizers}
            onSubmit={updateEventDetails}
          />
          <DeleteEventDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            onDelete={handleDeleteEvent}
            event={eventDetails}
          />
        </>
      ) : (
        <LoadingTable />
      )}
    </Container>
  );
};

export default EventDetails;
